/**
 * ComplyV's own legal pages — terms, privacy, refunds, contact.
 *
 * WHY THESE EXIST: a payment gateway will not activate an account until all
 * four are live and reachable without logging in. They are also, separately,
 * the promises we make to customers, so they are kept as text in the repo
 * where a change is reviewed like any other change.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THESE ARE DRAFTS. A lawyer must review them before launch.
 *
 * Every fact nobody has given us yet is a [[PLACEHOLDER]] in COMPANY below,
 * NOT a plausible guess — a made-up registered address on a legal page is
 * worse than an obvious blank. Fill COMPANY in and every page updates. While
 * any placeholder remains, each page shows an amber "not final" banner, so a
 * half-filled policy cannot go out quietly.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Editing: change the text here, nowhere else. LegalPage.jsx only renders.
 */

/**
 * The facts every document needs. Replace the [[…]] values; leave nothing
 * half-done. `isPlaceholder` below is what the banner keys off.
 */
export const COMPANY = {
  product: 'ComplyV',
  entity: '[[LEGAL ENTITY NAME — e.g. Appson Technologies Private Limited]]',
  cin: '[[COMPANY CIN]]',
  gstin: '[[GSTIN]]',
  address: '[[REGISTERED OFFICE ADDRESS, with city, state and PIN]]',
  supportEmail: '[[support@complyv.com]]',
  billingEmail: '[[billing@complyv.com]]',
  grievanceOfficer: '[[GRIEVANCE OFFICER NAME]]',
  grievanceEmail: '[[grievance@complyv.com]]',
  phone: '[[PHONE NUMBER]]',
  hours: 'Monday to Friday, 10:00 to 18:00 IST, excluding public holidays',
  jurisdiction: '[[CITY]], India',
  website: 'https://complyv.com',
  app: 'https://console.complyv.com',
  effectiveDate: '[[EFFECTIVE DATE]]',
  trialDays: 7,
};

export const isPlaceholder = (value) => /\[\[.+\]\]/.test(String(value || ''));

/** Which facts are still unfilled — the banner names them rather than hinting. */
export const unfilledFacts = (company = COMPANY) =>
  Object.entries(company)
    .filter(([, v]) => isPlaceholder(v))
    .map(([k]) => k);

const c = COMPANY;

// ─────────────────────────────────────────────────────────────────────────────

const TERMS = {
  id: 'terms',
  title: 'Terms of Service',
  intro:
    `These terms govern your use of ${c.product}, a compliance platform operated by ${c.entity} `
    + `("we", "us"). By creating an account or using the service you agree to them. If you are `
    + `agreeing on behalf of a firm or company, you confirm you are authorised to do so.`,
  sections: [
    {
      heading: '1. What ComplyV is, and what it is not',
      paragraphs: [
        `${c.product} helps company secretaries, chartered accountants and companies track compliance obligations, `
        + 'prepare documents, and maintain statutory registers under the Companies Act, 2013 and the SEBI '
        + '(Prohibition of Insider Trading) Regulations, 2015, among others.',
        'It is software, not a professional service. Nothing in the platform — including anything produced by our '
        + 'AI assistant — is legal, secretarial, accounting or tax advice, and no relationship of professional and '
        + 'client arises between you and us. Deadlines, penalty estimates, applicability decisions and drafts are '
        + 'aids to a qualified professional\'s judgement, not a substitute for it. You remain responsible for what '
        + 'you file and for verifying it against the relevant statute and the MCA, SEBI or other portal.',
        'We do not file anything with any authority on your behalf.',
      ],
    },
    {
      heading: '2. Your account',
      paragraphs: [
        'You must give accurate registration details and keep them current. You are responsible for everything done '
        + 'under your account and for keeping your credentials confidential. Tell us promptly if you believe an '
        + 'account has been compromised.',
        'Accounts are for named individuals. Sharing one login across several people is not permitted, both because '
        + 'it is charged per user and because the audit trail — who viewed a register, who recorded an entry — is '
        + 'part of what the platform exists to provide.',
      ],
    },
    {
      heading: '3. Free trial',
      paragraphs: [
        `New accounts receive a free trial of ${c.trialDays} days. During the trial you have access to the features `
        + 'described on the plans page.',
        'When the trial ends, features that create new work — the AI assistant, document drafting, and creating new '
        + 'register entries — stop until a subscription begins. Everything you created during the trial remains '
        + 'readable and downloadable. We do not withhold your own records.',
        'We may vary or withdraw trials for future customers at any time; this does not affect a trial already running.',
      ],
    },
    {
      heading: '4. Fees, billing and taxes',
      paragraphs: [
        'Subscription fees and what each plan includes are set out on our plans page. Fees are payable in advance for '
        + 'the billing period you choose.',
        'Prices are exclusive of Goods and Services Tax unless stated otherwise. GST is charged at the applicable rate '
        + 'and shown separately on your invoice. Give us your GSTIN before payment if you need it on the invoice; we '
        + 'cannot reissue an invoice to add it afterwards in every case.',
        'Payments are processed by a third-party payment gateway. We do not receive or store your full card details.',
        'Subscriptions renew automatically at the end of each period unless cancelled. We will attempt to collect on '
        + 'the renewal date; if payment fails we will retry and notify you, and the account may be restricted after the '
        + 'grace period described in the Refund and Cancellation Policy.',
        'We may change prices with at least 30 days\' notice. A change takes effect at your next renewal, never mid-period.',
      ],
    },
    {
      heading: '5. Your data',
      paragraphs: [
        'Everything you put into the platform — company records, documents, registers, insider lists — remains yours. '
        + 'We process it to provide the service, and for no other purpose. We do not sell it.',
        'You may export your data at any time while your subscription is active, in the formats the platform provides.',
        'How we handle personal data, and your rights under the Digital Personal Data Protection Act, 2023, are set out '
        + 'in our Privacy Policy, which forms part of these terms.',
      ],
    },
    {
      heading: '6. The AI assistant',
      paragraphs: [
        'The platform includes an AI assistant. To answer you, the content of your request — which may include documents '
        + 'you attach — is sent to a third-party AI provider for processing. This is described in the Privacy Policy.',
        'AI output can be wrong, incomplete or out of date. It must be checked by a qualified professional before it is '
        + 'relied on, signed or filed. Figures such as due dates and penalties are estimates and should be verified '
        + 'against the relevant portal.',
      ],
    },
    {
      heading: '7. Acceptable use',
      paragraphs: [
        'You must not: use the platform unlawfully, or to store or transmit anything unlawful; attempt to access another '
        + 'customer\'s data; probe, scan or test the security of the platform without our written permission; interfere '
        + 'with its operation; reverse-engineer it; resell or white-label it without an agreement; or use it to build a '
        + 'competing product.',
        'Automated access is permitted only through interfaces we provide for that purpose.',
      ],
    },
    {
      heading: '8. Availability and support',
      paragraphs: [
        'We aim to keep the platform available at all times, but we do not guarantee uninterrupted service. Maintenance '
        + 'is carried out outside Indian business hours where practicable, and we will give notice of anything that will '
        + 'take the service down for a significant period.',
        `Support is available by email at ${c.supportEmail} and through the platform, ${c.hours}.`,
      ],
    },
    {
      heading: '9. Our intellectual property',
      paragraphs: [
        'The platform, its software, design, templates and content are ours or our licensors\'. Your subscription is a '
        + 'right to use the platform, not a transfer of ownership. Documents you generate using it are yours to use.',
      ],
    },
    {
      heading: '10. Suspension and termination',
      paragraphs: [
        'You may cancel at any time, as described in the Refund and Cancellation Policy.',
        'We may suspend or terminate an account for non-payment after the grace period, for a breach of these terms, or '
        + 'where required by law. Except where the breach makes it impossible, we will tell you first and give you a '
        + 'reasonable opportunity to put it right.',
        'After termination you may export your data for 30 days. After that we delete or anonymise it, except where a '
        + 'law requires us to keep it — statutory registers, for example, carry their own retention periods.',
      ],
    },
    {
      heading: '11. Warranties and liability',
      paragraphs: [
        'The platform is provided "as is". To the extent the law allows, we exclude implied warranties, including '
        + 'fitness for a particular purpose.',
        'We are not liable for indirect or consequential loss, for loss of profit, or for penalties, interest or '
        + 'prosecution arising from a filing you made or failed to make. Our total liability in any twelve-month period '
        + 'is limited to the fees you paid us in that period.',
        'Nothing here limits liability that cannot be limited by law, including for fraud.',
      ],
    },
    {
      heading: '12. Changes to these terms',
      paragraphs: [
        'We may update these terms. For a material change we will give at least 30 days\' notice by email or in the '
        + 'platform. Continuing to use the service after a change takes effect means you accept it.',
      ],
    },
    {
      heading: '13. Governing law',
      paragraphs: [
        `These terms are governed by the laws of India. The courts at ${c.jurisdiction} have exclusive jurisdiction.`,
      ],
    },
    {
      heading: '14. Contact',
      paragraphs: [
        `${c.entity}, ${c.address}. Email ${c.supportEmail}.`,
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────

const PRIVACY = {
  id: 'privacy-policy',
  title: 'Privacy Policy',
  intro:
    `This policy explains what personal data ${c.entity} collects when you use ${c.product}, why, and what you can `
    + 'do about it. It is written to meet the Digital Personal Data Protection Act, 2023, under which we are a Data '
    + 'Fiduciary for the data described here.',
  sections: [
    {
      heading: '1. What we collect',
      paragraphs: [
        'Account data: your name, email address, phone number, professional membership details where you give them, '
        + 'and your password in hashed form.',
        'Data you enter about companies and people: company records, directors and their DINs, members, and — where you '
        + 'use the UPSI register — the names and PANs of insiders. You provide this; much of it is personal data '
        + 'belonging to people who are not our users, and you are responsible for having a basis to share it with us.',
        'Content: documents you create or upload, register entries, and the messages you send to the AI assistant.',
        'Usage data: pages visited, actions taken, IP address, browser and device information, and our audit log of '
        + 'who did what in the platform — which is itself a compliance feature.',
        'Payment data: we receive the fact of a payment, the amount and a transaction reference from our payment '
        + 'gateway. We do not receive or store your full card number.',
      ],
    },
    {
      heading: '2. Why we process it, and on what basis',
      paragraphs: [
        'To provide the service you asked for — this is the consent you give when you create an account and when you '
        + 'enter data, and in places a legitimate use under the Act.',
        'To bill you, and to meet our own tax and accounting obligations.',
        'To keep the platform secure, to investigate misuse, and to maintain the audit trail the compliance features '
        + 'depend on.',
        'To support you — including any ticket you raise and the conversation on it.',
        'To tell you about changes to the service. Marketing email, if we ever send any, is separate and you can opt out.',
        'We do not sell personal data, and we do not use your content to train AI models.',
      ],
    },
    {
      heading: '3. Who we share it with',
      paragraphs: [
        'Service providers who process data on our instructions, under contract: our cloud hosting provider, our email '
        + 'delivery provider, our payment gateway, and the AI providers described below.',
        'Where your own use of the platform sends something out — for example a Reg 3(2B) notice emailed to a person you '
        + 'recorded as receiving unpublished price-sensitive information, or a document you share with a colleague.',
        'Where the law requires it, or to establish or defend a legal claim.',
        'Not with anyone else, and never for their own marketing.',
      ],
    },
    {
      heading: '4. The AI assistant, and where your data goes',
      paragraphs: [
        'When you use the AI assistant, the text of your conversation and the text of any document you attach are sent '
        + 'to a third-party AI provider to generate a response. Those providers may process the data outside India.',
        'We send the minimum needed to answer, and we do not permit providers to use it to train their models.',
        'If you would rather a document never left our systems, do not attach it to the assistant — everything else in '
        + 'the platform works without it.',
      ],
    },
    {
      heading: '5. Transfers outside India',
      paragraphs: [
        'Some of our providers process data outside India. Where they do, we rely on contractual protections and on the '
        + 'Act\'s provisions for transfer. We will update this policy if the Central Government restricts transfers to a '
        + 'country we use.',
      ],
    },
    {
      heading: '6. How long we keep it',
      paragraphs: [
        'Account data: while your account exists, and for a reasonable period afterwards for legal and accounting purposes.',
        'Content you created: 30 days after termination, so you can export it, and then deleted or anonymised — unless a '
        + 'law requires longer. Statutory registers carry their own retention: an entry in the UPSI register is kept for '
        + 'eight years from the date of sharing, as Regulation 3(6) requires.',
        'Audit logs: retained as long as needed to serve their purpose, which for register access is the life of the '
        + 'register.',
        'AI conversations: kept only if you leave chat history on, and deletable by you at any time.',
      ],
    },
    {
      heading: '7. How we protect it',
      paragraphs: [
        'Data is encrypted in transit. Identifiers that the law treats as sensitive — PANs on insider records, mailbox '
        + 'passwords — are encrypted at rest and shown masked; revealing one is itself recorded in the audit trail.',
        'Access inside our team is limited to those who need it. No system is perfectly secure, and we do not claim '
        + 'otherwise; if a breach affects your personal data we will notify you and the Data Protection Board as the Act '
        + 'requires.',
      ],
    },
    {
      heading: '8. Your rights',
      paragraphs: [
        'Under the Act you may ask us for a summary of the personal data we hold about you and who we have shared it '
        + 'with; ask us to correct or complete it; ask us to erase it where we no longer need it; nominate someone to '
        + 'exercise your rights if you die or become incapacitated; and withdraw consent, which will usually mean we can '
        + 'no longer provide the service.',
        `Write to ${c.grievanceEmail} and we will respond within the time the Act allows. If you are not satisfied, you `
        + 'may complain to the Data Protection Board of India.',
      ],
    },
    {
      heading: '9. Grievance officer',
      paragraphs: [
        `${c.grievanceOfficer}, ${c.entity}, ${c.address}. Email ${c.grievanceEmail}. We acknowledge grievances within `
        + 'three working days and aim to resolve them within thirty.',
      ],
    },
    {
      heading: '10. Children',
      paragraphs: [
        'The platform is for professionals and businesses. We do not knowingly collect the personal data of anyone under '
        + '18, and we do not direct the service at children.',
      ],
    },
    {
      heading: '11. Cookies',
      paragraphs: [
        'We use cookies and similar storage to keep you signed in and to remember preferences such as which tab you were '
        + 'last on. We do not use advertising cookies.',
      ],
    },
    {
      heading: '12. Changes',
      paragraphs: [
        'We will post any change here and, if it is material, tell you by email before it takes effect.',
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────

const REFUND = {
  id: 'refund-policy',
  title: 'Refund and Cancellation Policy',
  intro:
    `This policy applies to subscriptions to ${c.product}. We have tried to write it plainly, because a refund policy `
    + 'that needs interpreting is not a policy.',
  sections: [
    {
      heading: '1. The free trial',
      paragraphs: [
        `Every new account gets ${c.trialDays} days free. Nothing is charged during the trial. If you do not subscribe, `
        + 'the account simply stops creating new work at the end of it — there is nothing to cancel and nothing to refund.',
      ],
    },
    {
      heading: '2. Cancelling',
      paragraphs: [
        'You may cancel at any time, from your account settings or by emailing us. Cancellation takes effect at the end '
        + 'of the period you have already paid for; you keep full access until then, and you are not billed again.',
        'We do not cancel your account for you when you stop using it. If you want it closed, tell us.',
      ],
    },
    {
      heading: '3. Refunds',
      paragraphs: [
        'We do not refund a period that has begun. Subscriptions are billed in advance, and cancelling part-way through '
        + 'a month or a year ends the renewal rather than refunding the part you have used.',
        'We will refund in full where: you were charged twice for the same period; you were charged after cancelling; or '
        + 'the platform was unavailable for a prolonged period through our fault and you could not use what you paid for.',
        'Where a refund is due, we return it to the original payment method within 7 to 10 working days of approving it. '
        + 'How long your bank then takes is outside our control.',
      ],
    },
    {
      heading: '4. Annual plans',
      paragraphs: [
        'An annual plan cancelled within 7 days of the first payment, and with no significant use in that time, is '
        + 'refunded in full. After that, the same rule as above applies: cancellation ends the renewal.',
      ],
    },
    {
      heading: '5. When a payment fails',
      paragraphs: [
        'We retry a failed renewal and email you. You keep access for a grace period of 7 days from the first failure. '
        + 'After that the account is restricted — you can still read and export everything you created, but not create '
        + 'new work — until payment succeeds.',
        'We do not delete anything because a payment failed.',
      ],
    },
    {
      heading: '6. Price changes',
      paragraphs: [
        'If we raise the price, we tell you at least 30 days before your renewal. If you would rather not continue, '
        + 'cancel before the renewal date and you are not charged the new price.',
      ],
    },
    {
      heading: '7. How to ask',
      paragraphs: [
        `Email ${c.billingEmail} with your account email and, if you have it, the payment reference. We reply within `
        + 'three working days.',
        'If you think a charge is wrong, please write to us before raising a chargeback — it is almost always faster.',
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────

const CONTACT = {
  id: 'contact',
  title: 'Contact Us',
  intro: `${c.entity}, the company behind ${c.product}.`,
  sections: [
    {
      heading: 'Registered office',
      paragraphs: [c.entity, c.address, `CIN ${c.cin}`, `GSTIN ${c.gstin}`],
    },
    {
      heading: 'Support',
      paragraphs: [
        `${c.supportEmail} — for anything about using the platform.`,
        'You can also raise a ticket from the help button inside the app, which is usually faster because it carries '
        + 'the context with it.',
        `We answer ${c.hours}, normally within one working day.`,
      ],
    },
    {
      heading: 'Billing',
      paragraphs: [`${c.billingEmail} — invoices, payments, refunds and GST details.`],
    },
    {
      heading: 'Privacy and grievances',
      paragraphs: [
        `${c.grievanceOfficer}, Grievance Officer — ${c.grievanceEmail}.`,
        'For anything about your personal data, or a complaint about how we have handled it. Acknowledged within three '
        + 'working days.',
      ],
    },
    {
      heading: 'Phone',
      paragraphs: [`${c.phone}, ${c.hours}.`],
    },
  ],
};

export const POLICIES = { terms: TERMS, 'privacy-policy': PRIVACY, 'refund-policy': REFUND, contact: CONTACT };

export const POLICY_LINKS = [
  { id: 'terms', label: 'Terms of Service' },
  { id: 'privacy-policy', label: 'Privacy Policy' },
  { id: 'refund-policy', label: 'Refund & Cancellation' },
  { id: 'contact', label: 'Contact Us' },
];
